import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMsal } from '@azure/msal-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'Admin' | 'Manager' | 'User';
  requireAnyRole?: ('Admin' | 'Manager' | 'User')[];
}

/**
 * Protected route component that enforces authentication and authorization
 * Redirects to /login if not authenticated
 * Redirects to /unauthorized if user lacks required role
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  requireAnyRole
}) => {
  const { user, isLoading } = useAuth();
  const { accounts } = useMsal();

  // Redirect to login if no MSAL account exists
  if (accounts.length === 0) {
    return <Navigate to="/login" replace state={{ from: window.location.pathname }} />;
  }

  // Show loading state while user data is being fetched
  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check role-based authorization if required
  if (requiredRole || requireAnyRole) {
    const roleHierarchy: Record<string, number> = { 
      Admin: 3, 
      Manager: 2, 
      User: 1 
    };
    
    const userLevel = roleHierarchy[user?.role as keyof typeof roleHierarchy] || 0;

    // Check single required role (hierarchical)
    if (requiredRole) {
      const requiredLevel = roleHierarchy[requiredRole];
      
      if (userLevel < requiredLevel) {
        return <Navigate to="/unauthorized" replace />;
      }
    }

    // Check if user has any of the required roles
    if (requireAnyRole) {
      const hasRequiredRole = requireAnyRole.some(role => {
        const requiredLevel = roleHierarchy[role];
        return userLevel >= requiredLevel;
      });

      if (!hasRequiredRole) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

/**
 * Unauthorized page component
 */
export const UnauthorizedPage: React.FC = () => {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
        <div className="text-red-600 text-6xl mb-4">
          <svg
            className="mx-auto h-24 w-24"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">403 - Access Denied</h1>
        <p className="text-gray-600 mb-6">
          You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </p>
        <button
          onClick={() => window.history.back()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};
